/**
 * @fileoverview App Component
 * @description Root component with all global providers
 *
 * Features:
 * - Session persistence on page refresh
 * - WebSocket connection management
 * - Global providers (React Query, Toast)
 */

import { useEffect, useCallback, useRef } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AppRouter } from './router';
import { ToastProvider, ToastContainer } from '@components/ui/Toast';
import { useAuthStore } from '@store/auth.store';
import { useSettingsStore } from '@store/settings.store';
import { authApi } from '@services/api/auth.api';
import { socketService } from '@services/websocket/socket.service';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 30,
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: 0,
    },
  },
});

// Development-only logging helper
const devLog = (...args: unknown[]): void => {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.log(...args);
  }
};

/**
 * Session Manager Component
 * Handles session validation on app start and WebSocket connection
 */
function SessionManager(): null {
  const accessToken = useAuthStore((state) => state.accessToken);
  const refreshToken = useAuthStore((state) => state.refreshToken);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const updateUser = useAuthStore((state) => state.updateUser);
  const updateAccessToken = useAuthStore((state) => state.updateAccessToken);
  const updateRefreshToken = useAuthStore((state) => state.updateRefreshToken);
  const logout = useAuthStore((state) => state.logout);
  const setLoading = useAuthStore((state) => state.setLoading);
  const isTokenExpired = useAuthStore((state) => state.isTokenExpired);
  const getTokenExpiresIn = useAuthStore((state) => state.getTokenExpiresIn);
  const loadSettings = useSettingsStore((state) => state.loadSettings);

  // Track if we've already validated the session
  const hasValidatedSession = useRef(false);
  // Track the refresh timer
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load settings on app start (public endpoint, no auth needed)
  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  /**
   * Validate session on app start
   * If there's a token in localStorage, verify it's still valid with the backend
   */
  const validateSession = useCallback(async () => {
    // Only run once on app start
    if (hasValidatedSession.current) return;
    hasValidatedSession.current = true;

    // If there's a token stored, validate it
    if (accessToken && isAuthenticated) {
      // Check if token is expired
      if (isTokenExpired()) {
        devLog('[SessionManager] Token expired, logging out');
        logout();
        setLoading(false);
        return;
      }

      try {
        // Validate token by fetching current user data
        const user = await authApi.me();
        updateUser(user);
        devLog('[SessionManager] Session validated successfully');
      } catch {
        devLog('[SessionManager] Session validation failed');
        // Token is invalid, clear auth state
        logout();
      }
    }

    setLoading(false);
  }, [accessToken, isAuthenticated, isTokenExpired, updateUser, logout, setLoading]);

  /**
   * Validate session on mount
   */
  useEffect(() => {
    void validateSession();
  }, [validateSession]);

  /**
   * Schedule automatic token refresh before expiration.
   * Refreshes 60 seconds before the token expires, then reconnects
   * the WebSocket with the new token so it never drops.
   */
  useEffect(() => {
    // Clear any existing timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    if (!isAuthenticated || !accessToken || !refreshToken) return;

    const scheduleRefresh = () => {
      const expiresIn = getTokenExpiresIn();
      // Refresh 60 seconds before expiration, minimum 5 seconds from now
      const refreshInSeconds = Math.max(expiresIn - 60, 5);

      devLog(
        `[SessionManager] Token expires in ${expiresIn}s, scheduling refresh in ${refreshInSeconds}s`,
      );

      refreshTimerRef.current = setTimeout(async () => {
        if (!refreshToken) return;

        try {
          devLog('[SessionManager] Refreshing token...');
          const newTokens = await authApi.refresh(refreshToken);

          // Update tokens in the store
          updateAccessToken(newTokens.accessToken, newTokens.expiresIn);
          if (newTokens.refreshToken) {
            updateRefreshToken(newTokens.refreshToken);
          }

          // Reconnect WebSocket with the fresh token
          devLog('[SessionManager] Token refreshed, reconnecting WebSocket...');
          socketService.disconnect();
          socketService.connect(newTokens.accessToken);

          devLog('[SessionManager] Token refresh complete');
        } catch {
          devLog('[SessionManager] Token refresh failed, logging out');
          logout();
        }
      }, refreshInSeconds * 1000);
    };

    scheduleRefresh();

    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [
    isAuthenticated,
    accessToken,
    refreshToken,
    getTokenExpiresIn,
    updateAccessToken,
    updateRefreshToken,
    logout,
  ]);

  /**
   * Manage WebSocket connection based on authentication state
   * and shared socket lifecycle callbacks
   */
  useEffect(() => {
    if (isAuthenticated && accessToken) {
      socketService.setCallbacks({
        onConnect: () => {
          devLog('[SessionManager] WebSocket connected');
        },
        onDisconnect: (reason) => {
          devLog('[SessionManager] WebSocket disconnected:', reason);
        },
        onError: (data) => {
          devLog('[SessionManager] WebSocket error:', data);
        },
      });

      // Real-time status/progress subscriptions are handled by useServices hook.
      devLog('[SessionManager] Connecting WebSocket...');
      socketService.connect(accessToken);
    } else {
      // Disconnect WebSocket when not authenticated
      if (socketService.getIsConnected()) {
        devLog('[SessionManager] Disconnecting WebSocket...');
        socketService.disconnect();
      }
    }

    // Cleanup on unmount
    return () => {
      if (socketService.getIsConnected()) {
        socketService.disconnect();
      }
    };
  }, [isAuthenticated, accessToken]);

  return null;
}

function App(): JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <SessionManager />
        <AppRouter />
        <ToastContainer position="top-right" />
      </ToastProvider>
    </QueryClientProvider>
  );
}

export default App;
